import { 
  Component, 
  Input, 
  Output, 
  EventEmitter, 
  ChangeDetectionStrategy, 
  OnInit,
  OnDestroy 
} from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SidenavGroup, SidenavItem } from '../../models/sidenav.interface';
import { SidenavEvent } from '../../enums/sidenav.enum';

@Component({
  selector: 'app-sidenav-group',
  templateUrl: './sidenav-group.component.html',
  styleUrls: ['./sidenav-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavGroupComponent implements OnInit, OnDestroy {
  @Input() group!: SidenavGroup;
  @Input() isCollapsed = false;
  @Input() activeRoute = '';
  @Input() notifications: { [key: string]: number } = {};
  @Input() lockedFeatures: string[] = [];

  @Output() itemClick = new EventEmitter<{
    event: SidenavEvent;
    item: SidenavItem;
    nativeEvent?: Event;
  }>();

  @Output() groupToggle = new EventEmitter<{
    event: SidenavEvent;
    group: SidenavGroup;
  }>();

  private readonly destroy$ = new Subject<void>();
  private readonly expandedSubject = new BehaviorSubject<boolean>(true);
  public readonly isExpanded$ = this.expandedSubject.asObservable();

  ngOnInit(): void {
    this.expandedSubject.next(this.group.isExpanded ?? true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.expandedSubject.complete();
  }

  public onItemClick(item: SidenavItem, event?: Event): void {
    this.itemClick.emit({
      event: SidenavEvent.ITEM_CLICKED,
      item,
      nativeEvent: event
    });
  }

  public onGroupHeaderClick(): void {
    if (this.group.isCollapsible !== false) {
      const newExpandedState = !this.expandedSubject.value;
      this.expandedSubject.next(newExpandedState);
      
      this.groupToggle.emit({
        event: SidenavEvent.GROUP_TOGGLED,
        group: { ...this.group, isExpanded: newExpandedState }
      });
    }
  }

  public getItemBadgeCount(item: SidenavItem): number {
    return this.notifications[item?.id] || item?.badgeCount || 0;
  }

  public isItemActive(item: SidenavItem): boolean {
    if (!item.route || !this.activeRoute) return false;

    // Strip query parameters and fragments from the active route
    const cleanActiveRoute = this.activeRoute.split('?')[0].split('#')[0];

    // Remove leading slash for consistent comparison
    const itemRoute = item.route.startsWith('/')
      ? item.route.substring(1)
      : item.route;
    const currentRoute = cleanActiveRoute.startsWith('/')
      ? cleanActiveRoute.substring(1)
      : cleanActiveRoute;

    // Exact match first
    if (currentRoute === itemRoute) return true;

    // Check if current route starts with item route (for sub-routes)
    if (currentRoute.startsWith(itemRoute + '/')) return true;

    // Special cases for complex routes
    if (itemRoute.includes('/') && currentRoute.includes('/')) {
      const itemBasePath = itemRoute.split('/')[0];
      const currentBasePath = currentRoute.split('/')[0];
      return (
        itemBasePath === currentBasePath && currentRoute.includes(itemRoute)
      );
    }

    return false;
  }

  /**
   * Get the appropriate icon for an item based on its active state
   */
  public getItemIcon(item: SidenavItem): string {
    if (this.isItemActive(item) && item.selectedIcon) {
      return item.selectedIcon;
    }
    return item.icon;
  }

  public isItemLocked(item: SidenavItem): boolean {
    return this.lockedFeatures.includes(item?.id);
  }

  public getVisibleItems(): SidenavItem[] {
    return this.group.items.filter(item => item?.isVisible !== false);
  }

  public trackByItemId(index: number, item: SidenavItem): string {
    return item.id;
  }
} 